import {
  Button,
  Card,
  Cascader,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnType } from 'antd/es/table';
import moment, { type Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';

import useService from '@/lib/hooks/useService';
import type { CommunityData } from '@/types/database/communityData';
import type CommunityGroups from '@/types/database/communityGroups';
import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';
import type { User } from '@/types/database/users';

import {
  emailValidator,
  getCustomInformation,
  getDataSource,
  getHouseholdNumberOptions,
  getPlanNames,
  getUserSubscriptionPlans,
  modalBodyStyles,
  showDeleteConfirm,
} from '../helper';
import type {
  ExtraUserInfoFormat,
  MembershipInformation,
  TableRecord,
} from '../types';
import styles from './index.module.scss';

const { Title } = Typography;
const { Item, useForm } = Form;

const InfoSetting = () => {
  const [form] = useForm<
    Omit<TableRecord, '_id' | 'householdList' | 'birthday'> & {
      birthday: Moment | null;
      communityName: string;
      householdNumbers: string[];
    }
  >();

  const [extraUserInfoFormat] = useService<
    Pick<CommunityData, 'extraUserInfoFormat'> & { _id: string }
  >({
    collectionName: 'communityData',
    query: {
      $select: ['extraUserInfoFormat'],
    },
  });
  const [users, updateUsers, usersService] = useService<
    User & MembershipInformation & { _id: string }
  >({
    collectionName: 'users',
  });
  const [communityGroups] = useService<CommunityGroups>({
    collectionName: 'communityGroups',
    query: {
      'householdNumbers.0': { $exists: true },
    },
  });
  const [subscriptionPlans] = useService<SubscriptionPlan>({
    collectionName: 'subscriptionPlan',
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [editingTableRecord, setEditingTableRecord] = useState<TableRecord>();
  const [selectedCommunityName, setSelectedCommunityName] = useState('');

  useEffect(() => {
    if (
      editingTableRecord &&
      editingTableRecord.householdList &&
      editingTableRecord.householdList.length !== 0
    ) {
      const formValues = {
        ...editingTableRecord,
        communityName: editingTableRecord.householdList[0].communityName,
        householdNumbers: [editingTableRecord.householdList[0].householdNumber],
        birthday: editingTableRecord.birthday
          ? moment(editingTableRecord.birthday)
          : null,
      };
      setSelectedCommunityName(
        editingTableRecord.householdList[0].communityName
      );
      form.setFieldsValue(formValues);
      return;
    }

    form.setFieldsValue({
      ...editingTableRecord,
      birthday: editingTableRecord?.birthday
        ? moment(editingTableRecord.birthday)
        : null,
    });
  }, [form, editingTableRecord]);

  const handleEditButtonClick = (information: TableRecord) => {
    setEditingTableRecord({ ...information });
    setIsOpenModal(true);
  };

  const communityNameOptions = communityGroups.map((community) => {
    return {
      label: community.name,
      value: community.name,
    };
  });

  const householdNumberOptions = getHouseholdNumberOptions(
    communityGroups,
    selectedCommunityName
  );

  const userSubscriptionPlans = getUserSubscriptionPlans(
    editingTableRecord,
    subscriptionPlans
  );

  const getColumns = useCallback(
    (customInformation: ExtraUserInfoFormat[], updateUsers: () => void) => {
      const customInfosColumns = customInformation.reduce(
        (customInfos: { title: string; dataIndex: string }[], information) => {
          if (information.isDefault === false) {
            customInfos.push({
              title: information.name,
              dataIndex: information.name,
            });
          }
          return customInfos;
        },
        []
      );

      const defaultColumns: ColumnType<TableRecord>[] = [
        {
          title: '社區',
          dataIndex: 'communityName',
          render: (_value, record) =>
            record.householdList && record.householdList.length !== 0
              ? record.householdList[0].communityName
              : '',
        },
        {
          title: '戶號',
          dataIndex: 'householdNumbers',
          render: (_value, record) =>
            record.householdList && record.householdList.length !== 0
              ? record.householdList[0].householdNumber
              : '',
        },
        {
          title: '方案',
          dataIndex: 'subscriptionPlans',
          render: (_value, record) => {
            if (record.subscriptionPlans) {
              const planNames = getPlanNames(
                subscriptionPlans,
                record.subscriptionPlans
              );
              return planNames.join('/');
            }
            return [];
          },
        },
        {
          title: '手機號碼',
          dataIndex: 'phoneNumber',
        },
        {
          title: '會員名',
          dataIndex: 'name',
        },
        {
          title: '生日',
          dataIndex: 'birthday',
          render: (_value, record) =>
            record.birthday ? record.birthday : null,
        },
        {
          title: '性別',
          dataIndex: 'gender',
        },
        {
          title: '電子郵件',
          dataIndex: 'realEmail',
        },
      ];

      const allColumns = defaultColumns.concat(customInfosColumns);

      const actionsColumn: ColumnType<TableRecord> = {
        title: '操作',
        dataIndex: 'actions',
        width: 200,
        fixed: 'right',
        render: (_value, record) => {
          const information = {
            ...record,
            birthday: record.birthday,
          };

          return (
            <Space>
              <Button onClick={() => handleEditButtonClick(information)}>
                編輯
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => showDeleteConfirm(record._id, updateUsers)}
              >
                刪除
              </Button>
            </Space>
          );
        },
      };

      allColumns.push(actionsColumn);

      return allColumns;
    },
    [subscriptionPlans]
  );

  const handleFormSubmit = async (
    formValues: Omit<TableRecord, '_id' | 'householdList' | 'birthday'> & {
      birthday: Moment | null;
      communityName: string;
      householdNumbers: string[];
    }
  ) => {
    const {
      communityName,
      householdNumbers,
      subscriptionPlans,
      phoneNumber,
      name,
      birthday,
      gender,
      realEmail,
      ...customQuestions
    } = formValues;

    const data = {
      householdList: [
        {
          communityName,
          householdNumber: householdNumbers.join('-'),
        },
      ],
      subscriptionPlans,
      phoneNumber,
      name,
      birthday: moment(birthday).format('YYYY/MM/DD'),
      gender,
      realEmail,
      userExtraInfo: {
        ...customQuestions,
      },
    };

    try {
      await usersService.patch(editingTableRecord?._id, data);
      updateUsers();
      closeModal();
      message.success('編輯個資成功!');
    } catch (error) {
      message.error('編輯個資失敗!');
    }
  };

  const handleValuesChange = (
    changedValues: Omit<TableRecord, '_id' | 'householdList'> & {
      communityName: string;
      householdNumbers: string[];
    }
  ) => {
    if ('communityName' in changedValues) {
      form.setFieldValue('householdNumbers', []);
      setSelectedCommunityName(changedValues.communityName);
    }
  };

  const closeModal = () => {
    if (selectedCommunityName) {
      setSelectedCommunityName('');
    }
    setIsOpenModal(false);
  };

  const checkEmptyFields = () => {
    if (
      selectedCommunityName &&
      (!form.getFieldValue('householdNumbers') ||
        form.getFieldValue('householdNumbers').length === 0)
    ) {
      return Promise.reject(new Error('請選擇戶號'));
    }
    if (
      !selectedCommunityName &&
      form.getFieldValue('householdNumbers') &&
      form.getFieldValue('householdNumbers').length !== 0
    ) {
      return Promise.reject(new Error('請選擇社區'));
    }

    return Promise.resolve();
  };

  const customInformation = getCustomInformation(extraUserInfoFormat);

  const columns = getColumns(customInformation, updateUsers);

  const dataSource = getDataSource(users, searchValue);

  return (
    <Space direction="vertical" className={styles.widthFull}>
      <Space>
        會員：
        <Input
          allowClear
          placeholder="請輸入會員手機號碼"
          className={styles.input}
          onChange={(event) => setSearchValue(event.target.value)}
        />
      </Space>
      <Card>
        <Space direction="vertical" className={styles.widthFull}>
          <Title level={5}>會員個資列表</Title>
          <Table
            bordered
            rowKey="_id"
            scroll={{ x: 'max-content' }}
            pagination={{ hideOnSinglePage: true }}
            columns={columns}
            dataSource={dataSource}
          />
        </Space>
        <Modal
          open={isOpenModal}
          title="編輯個資"
          centered
          destroyOnClose
          bodyStyle={modalBodyStyles}
          footer={null}
          onCancel={closeModal}
        >
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            onFinish={handleFormSubmit}
            onValuesChange={handleValuesChange}
          >
            <Item
              label={<span className={styles.formItemTitle}>手機號碼</span>}
              name="phoneNumber"
            >
              <span>{editingTableRecord?.phoneNumber}</span>
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>開通方案</span>}
              name="subscriptionPlans"
            >
              <span>{userSubscriptionPlans}</span>
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>社區</span>}
              name="communityName"
              rules={[{ validator: checkEmptyFields }]}
              validateTrigger="onSubmit"
            >
              <Select
                placeholder="請選擇社區"
                allowClear
                options={communityNameOptions}
              />
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>戶號</span>}
              name="householdNumbers"
              rules={[{ validator: checkEmptyFields }]}
            >
              <Cascader
                placeholder="請選擇戶號"
                options={householdNumberOptions}
                displayRender={(label) => label.join('-')}
              />
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>會員名</span>}
              name="name"
              rules={[{ required: true, message: '請輸入會員名' }]}
              validateTrigger="onSubmit"
            >
              <Input placeholder="請輸入會員名" />
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>生日</span>}
              name="birthday"
              rules={[{ required: true, message: '請選擇生日' }]}
            >
              <DatePicker
                placeholder="請選擇生日"
                inputReadOnly
                className={styles.datePicker}
              />
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>性別</span>}
              name="gender"
              rules={[{ required: true, message: '請輸入性別' }]}
            >
              <Input placeholder="請輸入性別" />
            </Item>
            <Item
              label={<span className={styles.formItemTitle}>電子郵件</span>}
              name="realEmail"
              rules={[
                {
                  required: true,
                  validator: (_rules, value) => emailValidator(value),
                },
              ]}
            >
              <Input placeholder="請輸入電子郵件" />
            </Item>
            {customInformation &&
              customInformation.map((info) => {
                return (
                  <Item
                    key={info.name}
                    label={
                      <span className={styles.formItemTitle}>{info.name}</span>
                    }
                    name={info.name}
                    rules={[
                      {
                        required: info.isRequired,
                        message: `請輸入${info.name}`,
                      },
                    ]}
                  >
                    <Input placeholder={`請輸入${info.name}`} />
                  </Item>
                );
              })}
            <Item>
              <Space className={styles.footerButtonsContainer}>
                <Button onClick={closeModal}>取消</Button>
                <Button type="primary" htmlType="submit">
                  確定
                </Button>
              </Space>
            </Item>
          </Form>
        </Modal>
      </Card>
    </Space>
  );
};

export default InfoSetting;
